/**
 * Fiche produit : galerie, choix de variante, quantité, ajout au panier en Ajax.
 */

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.product-gallery__track');
    this.slides = [...this.querySelectorAll('.product-gallery__slide')];
    this.thumbs = [...this.querySelectorAll('[data-thumb-index]')];
    this.counter = this.querySelector('[data-gallery-current]');
    if (!this.track || this.slides.length < 2) return;

    this.thumbs.forEach((thumb) =>
      thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.thumbIndex)))
    );

    let frame;
    this.track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => this.setActive(Math.round(this.track.scrollLeft / this.track.clientWidth)));
      },
      { passive: true }
    );
  }

  goTo(index, behavior = 'smooth') {
    const slide = this.slides[index];
    if (!slide) return;
    this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior });
    this.setActive(index);
  }

  showMedia(mediaId) {
    const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index > -1) this.goTo(index);
  }

  setActive(index) {
    this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', i === index ? 'true' : 'false'));
    if (this.counter) this.counter.textContent = index + 1;
  }
}
customElements.define('product-gallery', ProductGallery);

/* Effet 3D de la photo produit (souris uniquement : sur mobile, le doigt sert à faire défiler la galerie) */
(() => {
  const canTilt = window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!canTilt) return;
  const MAX = 7; // degrés
  document.addEventListener('pointermove', (event) => {
    const el = event.target.closest?.('[data-tilt]');
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    el.classList.add('is-tilting');
    el.style.setProperty('--tilt-y', `${(x - 0.5) * 2 * MAX}deg`);
    el.style.setProperty('--tilt-x', `${(0.5 - y) * 2 * MAX}deg`);
    el.style.setProperty('--tilt-scale', '1.06');
    el.style.setProperty('--shine-x', `${x * 100}%`);
    el.style.setProperty('--shine-y', `${y * 100}%`);
  });
  document.addEventListener(
    'pointerout',
    (event) => {
      const el = event.target.closest?.('[data-tilt]');
      if (!el || el.contains(event.relatedTarget)) return;
      el.classList.remove('is-tilting');
      ['--tilt-x', '--tilt-y', '--tilt-scale'].forEach((p) => el.style.removeProperty(p));
    }
  );
})();

class ProductInfo extends HTMLElement {
  connectedCallback() {
    const data = JSON.parse(this.querySelector('[data-product-json]').textContent);
    this.variants = data.variants;
    this.lowStock = data.lowStock || {};
    this.lowStockText = data.lowStockText || '';
    this.form = this.querySelector('[data-product-form]');
    this.idInput = this.querySelector('[data-variant-id]');
    this.addButton = this.querySelector('[data-add-button]');
    this.addLabel = this.querySelector('[data-add-label]');
    this.message = this.querySelector('[data-form-message]');
    this.quantityInput = this.querySelector('.quantity__input');
    this.strings = window.theme?.strings || {};
    this.currentVariant = this.variants.find((v) => String(v.id) === this.idInput?.value);

    this.querySelectorAll('.variant-picker input').forEach((input) =>
      input.addEventListener('change', () => this.onOptionChange())
    );

    this.querySelectorAll('[data-quantity]').forEach((btn) =>
      btn.addEventListener('click', () => {
        const step = btn.dataset.quantity === 'plus' ? 1 : -1;
        this.quantityInput.value = Math.max(1, (parseInt(this.quantityInput.value, 10) || 1) + step);
      })
    );

    this.form?.addEventListener('submit', (event) => this.onSubmit(event));
    this.setupStickyCart();
    window.theme.recentlyViewed?.add(this.dataset.productHandle);
  }

  /* Barre d'achat qui suit : visible quand le bouton principal est passé au-dessus de l'écran */
  setupStickyCart() {
    this.sticky = this.querySelector('[data-sticky-cart]');
    if (!this.sticky || !this.addButton || !('IntersectionObserver' in window)) return;
    this.stickyButton = this.sticky.querySelector('[data-sticky-add]');
    this.stickyLabel = this.sticky.querySelector('[data-sticky-label]');
    this.stickyButton.addEventListener('click', () => this.form.requestSubmit());

    const footer = document.querySelector('footer, .shopify-section-group-footer-group');
    let buttonAbove = false;
    let footerVisible = false;
    const toggle = () => {
      const visible = buttonAbove && !footerVisible;
      this.sticky.classList.toggle('is-visible', visible);
      this.sticky.setAttribute('aria-hidden', String(!visible));
      this.sticky.inert = !visible;
      document.documentElement.classList.toggle('has-sticky-cart', visible);
    };
    // Marge haute = hauteur de l'en-tête fixe, sous lequel le bouton disparaît
    const header = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 80;
    new IntersectionObserver(
      ([entry]) => {
        buttonAbove = !entry.isIntersecting && entry.boundingClientRect.top < header;
        toggle();
      },
      { rootMargin: `-${header}px 0px 0px 0px` }
    ).observe(this.addButton);
    if (footer) {
      new IntersectionObserver(([entry]) => {
        footerVisible = entry.isIntersecting;
        toggle();
      }).observe(footer);
    }
  }

  updateLowStock(variant) {
    const notice = this.querySelector('[data-low-stock]');
    if (!notice) return;
    const qty = variant ? this.lowStock[variant.id] : 0;
    notice.hidden = !qty;
    if (qty) notice.querySelector('[data-low-stock-text]').textContent = this.lowStockText.replace('999', qty);
  }

  selectedOptions() {
    return [...this.querySelectorAll('.variant-picker')].map(
      (fieldset) => fieldset.querySelector('input:checked')?.value
    );
  }

  onOptionChange() {
    const options = this.selectedOptions();
    const variant = this.variants.find((v) => v.options.every((value, i) => value === options[i]));
    this.currentVariant = variant;
    this.updateAvailability(variant);
    this.updateLowStock(variant);
    if (!variant) return;

    this.idInput.value = variant.id;
    this.updatePrice(variant);

    const size = this.querySelector('[data-selected-size]');
    if (size) size.textContent = `· ${variant.title}`;
    const stickyVariant = this.querySelector('[data-sticky-variant]');
    if (stickyVariant) stickyVariant.textContent = variant.title;
    const stickyPrice = this.querySelector('[data-sticky-price]');
    if (stickyPrice) stickyPrice.textContent = window.theme.formatMoney(variant.price);

    if (variant.featured_media) {
      this.querySelector('product-gallery')?.showMedia(variant.featured_media.id);
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  updatePrice(variant) {
    const price = this.querySelector('[data-price]');
    if (!price) return;
    const money = window.theme.formatMoney;
    if (variant.compare_at_price > variant.price) {
      price.innerHTML = `
        <span class="visually-hidden">${this.strings.salePrice}</span>
        <span class="price price--sale">${money(variant.price)}</span>
        <span class="visually-hidden">${this.strings.regularPrice}</span>
        <s class="price price--compare">${money(variant.compare_at_price)}</s>`;
    } else {
      price.innerHTML = `<span class="price">${money(variant.price)}</span>`;
    }
  }

  updateAvailability(variant) {
    if (!this.addButton) return;
    const available = Boolean(variant && variant.available);
    this.addButton.disabled = !available;
    this.addLabel.textContent = !variant
      ? this.strings.unavailable
      : available
        ? this.strings.addToCart
        : this.strings.soldOut;
    if (this.stickyButton) {
      this.stickyButton.disabled = !available;
      this.stickyLabel.textContent = this.addLabel.textContent;
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.addButton.disabled) return;

    this.addButton.classList.add('is-loading');
    this.addButton.setAttribute('aria-busy', 'true');
    this.message.hidden = true;

    try {
      await window.theme.cart.add(new FormData(this.form));
      if (this.dataset.cartType === 'page') {
        window.location.href = window.theme.routes.cart;
        return;
      }
      this.addLabel.textContent = this.strings.added;
      if (this.stickyLabel) this.stickyLabel.textContent = this.strings.added;
      setTimeout(() => this.updateAvailability(this.currentVariant), 2200);
    } catch (error) {
      this.message.textContent = error.message || this.strings.error;
      this.message.hidden = false;
    } finally {
      this.addButton.classList.remove('is-loading');
      this.addButton.removeAttribute('aria-busy');
    }
  }
}
customElements.define('product-info', ProductInfo);
